import React, { useEffect, useId, useRef, useState } from "react";

// "Trouble signing in?" on the signed-out welcome card.
//
// Fixed, plain steps (no AI), and a small "Have us check my login" form that
// puts a request in the office's Admin -> Notifications inbox. It lives on OUR
// card, not inside Clerk's sign-in page, and touches nothing of Clerk's: the
// person using it can't sign in, so the form is a plain fetch to the backend's
// public endpoint (POST /api/public/login-help-request). Nothing is sent until
// the form is submitted.
//
// Every step below is true of this site (the invitation letter's "How to sign
// in" block and the office's experience that codes land in Spam). The address
// the codes come from isn't known for certain, so it isn't named.

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";
export const MAX_NAME_CHARS = 100;
export const MAX_EMAIL_CHARS = 254;
export const MAX_NOTE_CHARS = 1000;
// A minute, not less: the server is on a free plan that sleeps when nobody has
// used the site for a while, and waking it can take most of that.
export const REQUEST_TIMEOUT_MS = 60000;
export const SLOW_HINT = "This can take a moment. Please keep this page open.";

export const SENT_MESSAGE = "Thanks — the office will check your login and contact you.";
export const FAILED_MESSAGE =
  "That didn't send. Please try again, or reply to your invitation email.";

// The same plain shape check the backend uses (not proof the mailbox exists).
const EMAIL_PATTERN = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;

async function sendRequest(body) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_URL}/public/login-help-request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (res.ok) return { ok: true };
    let data = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    // Only this form's own messages ("Please type your name.", "We've received
    // a lot of requests...") are shown. A server error or an HTML error page
    // gets the plain "didn't send" message instead.
    if ((res.status === 400 || res.status === 429) && data && typeof data.error === "string" && data.error.trim()) {
      return { ok: false, error: data.error };
    }
    return { ok: false, error: FAILED_MESSAGE };
  } catch {
    return { ok: false, error: FAILED_MESSAGE };
  } finally {
    clearTimeout(timer);
  }
}

const linkClass =
  "underline underline-offset-2 text-dupontBlue dark:text-darkGold hover:text-dupontGoldDk dark:hover:text-darkText " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-dupontGold rounded-sm cursor-pointer";

const fieldClass =
  "w-full rounded-lg border border-dupontGold/50 dark:border-darkGold/20 bg-white dark:bg-darkCard px-3 py-2 " +
  "text-[16px] text-dupontBlue dark:text-darkText placeholder-dupontGray/70 dark:placeholder-darkTextSecondary " +
  "focus:outline-none focus:border-dupontGold focus:ring-1 focus:ring-dupontGold";

const labelClass = "text-[14px] font-semibold text-dupontBlue dark:text-darkText";
const headingClass = "font-display text-[17px] font-semibold text-dupontBlue dark:text-darkText";

function CheckMyLoginForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [website, setWebsite] = useState(""); // the honeypot: people never see it
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const nameId = useId();
  const emailId = useId();
  const noteId = useId();
  const trapId = useId();
  const headingId = useId();
  const sentRef = useRef(null);

  // The thanks message replaces the whole form, including the button that had
  // focus. Move focus onto the message so keyboard and screen-reader users
  // aren't dropped back at the top of the page.
  useEffect(() => {
    if (sent) sentRef.current?.focus();
  }, [sent]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const cleanName = name.trim();
    const cleanEmail = email.trim();
    if (!cleanName) {
      setError("Please type your name.");
      return;
    }
    if (!cleanEmail) {
      setError("Please type the email address your invitation was sent to.");
      return;
    }
    if (!EMAIL_PATTERN.test(cleanEmail)) {
      setError("That email address doesn't look right. Please check it.");
      return;
    }
    setError("");
    setBusy(true);
    const result = await sendRequest({
      name: cleanName,
      email: cleanEmail,
      note: note.trim(),
      website,
    });
    setBusy(false);
    if (result.ok) setSent(true);
    else setError(result.error);
  };

  if (sent) {
    return (
      <p
        ref={sentRef}
        role="status"
        tabIndex={-1}
        className="mt-3 outline-none rounded-lg border border-dupontGold/50 dark:border-darkGold/30 bg-white dark:bg-darkCard px-3 py-3 text-[15px] leading-relaxed text-dupontBlue dark:text-darkText"
      >
        {SENT_MESSAGE}
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate aria-labelledby={headingId} className="flex flex-col gap-3">
      <h3 id={headingId} className={headingClass}>
        Have us check my login
      </h3>
      <p className="-mt-2 text-[14px] leading-relaxed text-dupontBlue/80 dark:text-darkText/80">
        Tried the steps and still can't get in? Send this and the office will
        check your login and contact you. Don't include your code or password.
      </p>

      <div className="flex flex-col gap-1">
        <label htmlFor={nameId} className={labelClass}>
          Your name
        </label>
        <input
          id={nameId}
          type="text"
          autoComplete="name"
          maxLength={MAX_NAME_CHARS}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={fieldClass}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={emailId} className={labelClass}>
          The email you were invited with
        </label>
        <input
          id={emailId}
          type="email"
          inputMode="email"
          autoComplete="email"
          maxLength={MAX_EMAIL_CHARS}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={fieldClass}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={noteId} className={labelClass}>
          Anything we should know? <span className="font-normal">(optional)</span>
        </label>
        <textarea
          id={noteId}
          rows={2}
          maxLength={MAX_NOTE_CHARS}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. The code never arrives"
          className={`${fieldClass} resize-none`}
        />
      </div>

      {/* The honeypot. Hidden from people and from screen readers, and out of
          the Tab order; only a form-filling bot fills it in. Its name means
          nothing to browser autofill or password managers (a field named
          "website" can be autofilled, which would quietly drop a real request);
          it is still sent to the server as "website". */}
      <div aria-hidden="true" className="absolute -left-[10000px] top-auto w-px h-px overflow-hidden">
        <label htmlFor={trapId}>Leave this empty</label>
        <input
          id={trapId}
          type="text"
          name="dp_check_leave_empty"
          tabIndex={-1}
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      {error && (
        <p role="alert" className="text-[14px] text-red-700 dark:text-red-400">
          {error}
        </p>
      )}

      <div>
        <button
          type="submit"
          disabled={busy}
          className="rounded-full bg-dupontBlue dark:bg-darkGold text-white dark:text-darkPrimary px-5 py-2 text-[15px] font-semibold disabled:opacity-50 disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-dupontGold focus-visible:ring-offset-2 cursor-pointer"
        >
          {busy ? "Sending…" : "Send request"}
        </button>
        {busy && (
          <p className="mt-2 text-[14px] text-dupontBlue/80 dark:text-darkText/80">{SLOW_HINT}</p>
        )}
      </div>
    </form>
  );
}

function SignInHelper() {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef(null);
  const panelRef = useRef(null);
  const wasOpen = useRef(false);
  const panelId = useId();

  // Opening moves focus to the panel; closing hands it back to the link.
  useEffect(() => {
    if (open) panelRef.current?.focus();
    else if (wasOpen.current) toggleRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  const onPanelKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div className="mt-5 relative text-[14px] text-dupontGray dark:text-darkTextSecondary">
      <p>
        <button
          ref={toggleRef}
          type="button"
          className={linkClass}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
        >
          Trouble signing in?
        </button>
      </p>

      {open && (
        <section
          id={panelId}
          ref={panelRef}
          tabIndex={-1}
          aria-label="Sign-in help"
          onKeyDown={onPanelKeyDown}
          className="mt-4 text-left outline-none rounded-xl border border-dupontGold/40 dark:border-darkGold/20 bg-dupontCream/70 dark:bg-darkPrimary/60 px-4 py-4 text-[15px] leading-relaxed text-dupontBlue dark:text-darkText"
        >
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-display text-[19px] font-semibold text-dupontBlue dark:text-darkText">
              Signing in, step by step
            </h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close sign-in help"
              className="shrink-0 -mt-1 w-9 h-9 rounded-full text-[20px] leading-none text-dupontGray dark:text-darkTextSecondary hover:bg-dupontGold/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-dupontGold cursor-pointer"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <ol className="mt-2 list-decimal pl-5 flex flex-col gap-1.5">
            <li>Click "Sign in" and type the email address your invitation was sent to.</li>
            <li>Leave the password box blank and click "Forgot password?" (the small link just above the password box).</li>
            <li>Choose "Reset your password". We'll email you a short code.</li>
            <li>Type the code in, then make your own password. Next time, sign in with your email and that password.</li>
          </ol>

          <h3 className={`mt-4 ${headingClass}`}>No code, or it doesn't work?</h3>
          <ul className="mt-1 list-disc pl-5 flex flex-col gap-1.5">
            <li>Look in your Spam or Junk folder. In Gmail, also check the Promotions and Updates tabs.</li>
            <li>
              If it's there, mark it "Not spam" and add the sender to your
              contacts, so future codes land in your inbox.
            </li>
            <li>Codes expire after a short time. Ask for a new one and use the code from the newest email.</li>
            <li>Use the exact email address you were invited with.</li>
            <li>
              Seeing a message that your password "may be compromised"? That's
              normal and expected. Click "Forgot password?" and make your own
              password, as in the steps above.
            </li>
          </ul>

          <p className="mt-3 font-semibold">
            Never share your code or password with anyone. No one from the
            family or the office will ever ask for it.
          </p>

          <div className="mt-5 pt-4 border-t border-dupontGold/30 dark:border-darkGold/15">
            <CheckMyLoginForm />
          </div>
        </section>
      )}

      <p className="mt-3 text-[13px] text-dupontGray/90 dark:text-darkTextSecondary/90">
        Still stuck? Reply to your invitation email and we'll help.
      </p>
    </div>
  );
}

export default SignInHelper;
